import { ModuleTypeEnum } from '../types/module'

/**
 * User-friendly module display names
 */
export const MODULE_DISPLAY_NAMES: Partial<Record<ModuleTypeEnum, string>> = {
  [ModuleTypeEnum.PROJECT]: 'Projects',
  [ModuleTypeEnum.TASK]: 'Tasks',
  [ModuleTypeEnum.TICKET]: 'Tickets',
  [ModuleTypeEnum.TASK_GROUP]: 'Task groups',
  [ModuleTypeEnum.ENTITY]: 'Entities',
  [ModuleTypeEnum.DEAL]: 'Deals',
  [ModuleTypeEnum.EVENT]: 'Events',
  [ModuleTypeEnum.CONTRACT]: 'Contracts',
  [ModuleTypeEnum.INVOICE]: 'Invoices',
  [ModuleTypeEnum.QUOTE]: 'Quotes',
  [ModuleTypeEnum.EXPENSE]: 'Expenses',
  [ModuleTypeEnum.THREAD]: 'Discussions',
  [ModuleTypeEnum.THREAD_COMMENT]: 'Comments',
  [ModuleTypeEnum.TIME_ENTRY]: 'Timesheets',
  [ModuleTypeEnum.TEAM]: 'Team',
  [ModuleTypeEnum.INVENTORY]: 'Inventory',
  [ModuleTypeEnum.SUPPLIER]: 'Suppliers',
  [ModuleTypeEnum.MEMBER]: 'Members',
  [ModuleTypeEnum.DOCUMENT]: 'Documents',
  [ModuleTypeEnum.ORGANIZATION]: 'My Organization',
  [ModuleTypeEnum.TODAY]: 'Today',
  [ModuleTypeEnum.CUSTOM_FIELD]: 'Custom fields',
  [ModuleTypeEnum.CUSTOM_COLLECTION]: 'Custom collections',
  [ModuleTypeEnum.PERMISSION]: 'Permissions',
  [ModuleTypeEnum.PERMISSION_SET]: 'Permission sets',
  [ModuleTypeEnum.FEATURE_FLAG]: 'Feature flags',
  [ModuleTypeEnum.INTEGRATION]: 'Integrations',
  [ModuleTypeEnum.AI]: 'AI',
  [ModuleTypeEnum.NOTE]: 'Notes',
  [ModuleTypeEnum.CONTACT]: 'Contacts',
  [ModuleTypeEnum.PROFILE]: 'Profile',
  [ModuleTypeEnum.CALENDAR]: 'Calendar',
  [ModuleTypeEnum.CYCLE_INSTANCE]: 'Cycles',
  [ModuleTypeEnum.CYCLE_TEMPLATE]: 'Cycle templates',
  [ModuleTypeEnum.FORM]: 'Forms',
  [ModuleTypeEnum.FORM_SUBMISSION]: 'Form submissions',
  [ModuleTypeEnum.WORKFLOW]: 'Workflows',
  [ModuleTypeEnum.ANALYTICS]: 'Analytics',
  // Display-only rename (billing schedule pass Phase 2.1, D3); seeded
  // permission-preset names keep the old noun.
  [ModuleTypeEnum.BILLING_AUTHORIZATION]: 'Client authorization',
  [ModuleTypeEnum.MARKETING]: 'Marketing',
  [ModuleTypeEnum.KNOWLEDGE_BASE]: 'Knowledge base',
  [ModuleTypeEnum.WORKSTREAM]: 'Workstreams',
  [ModuleTypeEnum.FINANCIALS]: 'Profitability',
}

/**
 * Function types for cross-module features
 */
export type FunctionType =
  | 'documents'
  | 'discussions'
  | 'activity'
  | 'settings'
  | 'finance'
  | 'team'

/**
 * User-friendly function display names
 */
export const FUNCTION_DISPLAY_NAMES: Record<FunctionType, string> = {
  documents: 'Documents',
  discussions: 'Discussions',
  activity: 'Activity',
  settings: 'Settings',
  finance: 'Finance',
  team: 'Team',
}

/**
 * Color scheme definition with HSL values
 */
export interface ColorScheme {
  primary: { h: number; s: number; l: number }
  light: { h: number; s: number; l: number }
  bg: { h: number; s: number; l: number }
  border: { h: number; s: number; l: number }
  hover: { h: number; s: number; l: number }
  icon: { h: number; s: number; l: number }
  text: { h: number; s: number; l: number }
  gradientFrom?: { h: number; s: number; l: number }
  gradientTo?: { h: number; s: number; l: number }
}

/**
 * Color scheme definition for dark mode
 */
export interface DarkColorScheme extends ColorScheme {}

/**
 * Complete theme including light and dark modes
 */
export interface ColorTheme {
  light: ColorScheme
  dark: DarkColorScheme
}

/**
 * Module color themes with HSL values
 */
export const MODULE_COLOR_THEMES: Partial<Record<ModuleTypeEnum, ColorTheme>> =
  {
    [ModuleTypeEnum.PROJECT]: {
      light: {
        primary: { h: 32, s: 81, l: 55 },
        light: { h: 32, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 32, s: 81, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 32, s: 85, l: 65 },
        light: { h: 32, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 32, s: 85, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.ENTITY]: {
      light: {
        primary: { h: 217, s: 91, l: 60 },
        light: { h: 217, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 217, s: 91, l: 60 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 217, s: 85, l: 70 },
        light: { h: 217, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 217, s: 85, l: 75 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.TASK]: {
      light: {
        primary: { h: 142, s: 71, l: 45 },
        light: { h: 142, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 142, s: 71, l: 45 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 142, s: 75, l: 60 },
        light: { h: 142, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 142, s: 75, l: 65 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.TICKET]: {
      light: {
        primary: { h: 199, s: 89, l: 48 },
        light: { h: 199, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 199, s: 89, l: 48 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 199, s: 85, l: 65 },
        light: { h: 199, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 199, s: 85, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.TASK_GROUP]: {
      light: {
        primary: { h: 198, s: 83, l: 46 },
        light: { h: 198, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 198, s: 83, l: 46 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 198, s: 80, l: 66 },
        light: { h: 198, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 198, s: 80, l: 72 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.DEAL]: {
      light: {
        primary: { h: 45, s: 93, l: 47 },
        light: { h: 45, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 45, s: 93, l: 47 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 45, s: 90, l: 65 },
        light: { h: 45, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 45, s: 90, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.EVENT]: {
      light: {
        primary: { h: 348, s: 83, l: 47 },
        light: { h: 348, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 348, s: 83, l: 47 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 348, s: 80, l: 65 },
        light: { h: 348, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 348, s: 80, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CONTRACT]: {
      light: {
        primary: { h: 250, s: 75, l: 60 },
        light: { h: 250, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 250, s: 75, l: 60 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 250, s: 80, l: 70 },
        light: { h: 250, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 250, s: 80, l: 75 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.INVOICE]: {
      light: {
        primary: { h: 39, s: 85, l: 53 },
        light: { h: 39, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 39, s: 85, l: 53 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 39, s: 85, l: 65 },
        light: { h: 39, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 39, s: 85, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.EXPENSE]: {
      light: {
        primary: { h: 18, s: 82, l: 54 },
        light: { h: 18, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 18, s: 82, l: 54 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 18, s: 82, l: 66 },
        light: { h: 18, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 18, s: 82, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.THREAD]: {
      light: {
        primary: { h: 291, s: 64, l: 42 },
        light: { h: 291, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 291, s: 64, l: 42 },
        text: { h: 210, s: 15, l: 25 },
        gradientFrom: { h: 291, s: 50, l: 90 },
        gradientTo: { h: 300, s: 50, l: 88 },
      },
      dark: {
        primary: { h: 291, s: 70, l: 65 },
        light: { h: 291, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 291, s: 70, l: 70 },
        text: { h: 210, s: 20, l: 80 },
        gradientFrom: { h: 291, s: 30, l: 20 },
        gradientTo: { h: 300, s: 30, l: 18 },
      },
    },
    [ModuleTypeEnum.THREAD_COMMENT]: {
      light: {
        primary: { h: 291, s: 64, l: 42 },
        light: { h: 291, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 291, s: 64, l: 42 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 291, s: 70, l: 65 },
        light: { h: 291, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 291, s: 70, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.TIME_ENTRY]: {
      light: {
        primary: { h: 158, s: 64, l: 52 },
        light: { h: 158, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 158, s: 64, l: 52 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 158, s: 70, l: 65 },
        light: { h: 158, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 158, s: 70, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.TEAM]: {
      light: {
        primary: { h: 180, s: 55, l: 45 },
        light: { h: 180, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 180, s: 55, l: 45 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 180, s: 60, l: 60 },
        light: { h: 180, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 180, s: 60, l: 65 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.INVENTORY]: {
      light: {
        primary: { h: 120, s: 65, l: 45 },
        light: { h: 120, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 120, s: 65, l: 45 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 120, s: 70, l: 55 },
        light: { h: 120, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 120, s: 70, l: 60 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.SUPPLIER]: {
      light: {
        primary: { h: 280, s: 70, l: 50 },
        light: { h: 280, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 280, s: 70, l: 50 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 280, s: 75, l: 60 },
        light: { h: 280, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 280, s: 75, l: 65 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.MEMBER]: {
      light: {
        primary: { h: 190, s: 81, l: 67 },
        light: { h: 190, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 190, s: 81, l: 67 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 190, s: 80, l: 70 },
        light: { h: 190, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 190, s: 80, l: 75 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.DOCUMENT]: {
      light: {
        primary: { h: 100, s: 42, l: 45 },
        light: { h: 100, s: 100, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 100, s: 42, l: 45 },
        text: { h: 210, s: 15, l: 25 },
        gradientFrom: { h: 100, s: 45, l: 90 },
        gradientTo: { h: 142, s: 45, l: 88 },
      },
      dark: {
        primary: { h: 100, s: 50, l: 55 },
        light: { h: 100, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 100, s: 50, l: 60 },
        text: { h: 210, s: 20, l: 80 },
        gradientFrom: { h: 100, s: 30, l: 20 },
        gradientTo: { h: 142, s: 30, l: 18 },
      },
    },
    [ModuleTypeEnum.ORGANIZATION]: {
      light: {
        primary: { h: 215, s: 25, l: 27 },
        light: { h: 215, s: 30, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 215, s: 25, l: 27 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 215, s: 20, l: 60 },
        light: { h: 215, s: 15, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 215, s: 20, l: 65 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.TODAY]: {
      light: {
        primary: { h: 220, s: 15, l: 45 },
        light: { h: 220, s: 20, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 220, s: 15, l: 45 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 220, s: 15, l: 60 },
        light: { h: 220, s: 15, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 220, s: 15, l: 65 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.INTEGRATION]: {
      light: {
        primary: { h: 260, s: 60, l: 50 },
        light: { h: 260, s: 60, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 260, s: 60, l: 50 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 260, s: 60, l: 65 },
        light: { h: 260, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 260, s: 60, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.AI]: {
      light: {
        primary: { h: 270, s: 70, l: 55 },
        light: { h: 270, s: 70, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 270, s: 70, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 270, s: 70, l: 65 },
        light: { h: 270, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 270, s: 70, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.NOTE]: {
      light: {
        primary: { h: 45, s: 80, l: 55 },
        light: { h: 45, s: 80, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 45, s: 80, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 45, s: 80, l: 65 },
        light: { h: 45, s: 40, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 45, s: 80, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CUSTOM_FIELD]: {
      light: {
        primary: { h: 300, s: 60, l: 55 },
        light: { h: 300, s: 60, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 300, s: 60, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 300, s: 60, l: 65 },
        light: { h: 300, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 300, s: 60, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CUSTOM_COLLECTION]: {
      light: {
        primary: { h: 320, s: 60, l: 55 },
        light: { h: 320, s: 60, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 320, s: 60, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 320, s: 60, l: 65 },
        light: { h: 320, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 320, s: 60, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.PERMISSION]: {
      light: {
        primary: { h: 340, s: 60, l: 55 },
        light: { h: 340, s: 60, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 340, s: 60, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 340, s: 60, l: 65 },
        light: { h: 340, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 340, s: 60, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.PERMISSION_SET]: {
      light: {
        primary: { h: 360, s: 60, l: 55 },
        light: { h: 360, s: 60, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 360, s: 60, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 360, s: 60, l: 65 },
        light: { h: 360, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 360, s: 60, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.FEATURE_FLAG]: {
      light: {
        primary: { h: 380, s: 60, l: 55 },
        light: { h: 380, s: 60, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 380, s: 60, l: 55 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 380, s: 60, l: 65 },
        light: { h: 380, s: 30, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 380, s: 60, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CONTACT]: {
      light: {
        primary: { h: 200, s: 70, l: 50 },
        light: { h: 200, s: 70, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 200, s: 70, l: 50 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 200, s: 70, l: 65 },
        light: { h: 200, s: 40, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 200, s: 70, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.PROFILE]: {
      light: {
        primary: { h: 220, s: 60, l: 50 },
        light: { h: 220, s: 60, l: 96 },
        bg: { h: 220, s: 10, l: 98 },
        border: { h: 220, s: 15, l: 88 },
        hover: { h: 220, s: 15, l: 96 },
        icon: { h: 220, s: 60, l: 50 },
        text: { h: 220, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 220, s: 60, l: 65 },
        light: { h: 220, s: 40, l: 25 },
        bg: { h: 220, s: 15, l: 8 },
        border: { h: 220, s: 20, l: 20 },
        hover: { h: 220, s: 20, l: 15 },
        icon: { h: 220, s: 60, l: 70 },
        text: { h: 220, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CALENDAR]: {
      light: {
        primary: { h: 200, s: 80, l: 50 },
        light: { h: 200, s: 80, l: 96 },
        bg: { h: 200, s: 10, l: 98 },
        border: { h: 200, s: 15, l: 88 },
        hover: { h: 200, s: 15, l: 96 },
        icon: { h: 200, s: 80, l: 50 },
        text: { h: 200, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 200, s: 80, l: 65 },
        light: { h: 200, s: 40, l: 25 },
        bg: { h: 200, s: 15, l: 8 },
        border: { h: 200, s: 20, l: 20 },
        hover: { h: 200, s: 20, l: 15 },
        icon: { h: 200, s: 80, l: 70 },
        text: { h: 200, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CYCLE_INSTANCE]: {
      light: {
        primary: { h: 280, s: 70, l: 55 },
        light: { h: 280, s: 70, l: 96 },
        bg: { h: 280, s: 10, l: 98 },
        border: { h: 280, s: 15, l: 88 },
        hover: { h: 280, s: 15, l: 96 },
        icon: { h: 280, s: 70, l: 55 },
        text: { h: 280, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 280, s: 70, l: 65 },
        light: { h: 280, s: 40, l: 25 },
        bg: { h: 280, s: 15, l: 8 },
        border: { h: 280, s: 20, l: 20 },
        hover: { h: 280, s: 20, l: 15 },
        icon: { h: 280, s: 70, l: 70 },
        text: { h: 280, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.CYCLE_TEMPLATE]: {
      light: {
        primary: { h: 290, s: 65, l: 50 },
        light: { h: 290, s: 65, l: 96 },
        bg: { h: 290, s: 10, l: 98 },
        border: { h: 290, s: 15, l: 88 },
        hover: { h: 290, s: 15, l: 96 },
        icon: { h: 290, s: 65, l: 50 },
        text: { h: 290, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 290, s: 65, l: 65 },
        light: { h: 290, s: 35, l: 25 },
        bg: { h: 290, s: 15, l: 8 },
        border: { h: 290, s: 20, l: 20 },
        hover: { h: 290, s: 20, l: 15 },
        icon: { h: 290, s: 65, l: 70 },
        text: { h: 290, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.FORM]: {
      light: {
        primary: { h: 250, s: 70, l: 55 },
        light: { h: 250, s: 70, l: 96 },
        bg: { h: 250, s: 10, l: 98 },
        border: { h: 250, s: 15, l: 88 },
        hover: { h: 250, s: 15, l: 96 },
        icon: { h: 250, s: 70, l: 55 },
        text: { h: 250, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 250, s: 70, l: 65 },
        light: { h: 250, s: 40, l: 25 },
        bg: { h: 250, s: 15, l: 8 },
        border: { h: 250, s: 20, l: 20 },
        hover: { h: 250, s: 20, l: 15 },
        icon: { h: 250, s: 70, l: 70 },
        text: { h: 250, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.FORM_SUBMISSION]: {
      light: {
        primary: { h: 250, s: 60, l: 50 },
        light: { h: 250, s: 60, l: 96 },
        bg: { h: 250, s: 10, l: 98 },
        border: { h: 250, s: 15, l: 88 },
        hover: { h: 250, s: 15, l: 96 },
        icon: { h: 250, s: 60, l: 50 },
        text: { h: 250, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 250, s: 60, l: 60 },
        light: { h: 250, s: 35, l: 25 },
        bg: { h: 250, s: 15, l: 8 },
        border: { h: 250, s: 20, l: 20 },
        hover: { h: 250, s: 20, l: 15 },
        icon: { h: 250, s: 60, l: 65 },
        text: { h: 250, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.WORKFLOW]: {
      light: {
        primary: { h: 210, s: 70, l: 50 },
        light: { h: 210, s: 70, l: 96 },
        bg: { h: 210, s: 10, l: 98 },
        border: { h: 210, s: 15, l: 88 },
        hover: { h: 210, s: 15, l: 96 },
        icon: { h: 210, s: 70, l: 50 },
        text: { h: 210, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 210, s: 70, l: 65 },
        light: { h: 210, s: 35, l: 25 },
        bg: { h: 210, s: 15, l: 8 },
        border: { h: 210, s: 20, l: 20 },
        hover: { h: 210, s: 20, l: 15 },
        icon: { h: 210, s: 70, l: 70 },
        text: { h: 210, s: 20, l: 80 },
      },
    },
    [ModuleTypeEnum.ANALYTICS]: {
      light: {
        primary: { h: 270, s: 60, l: 50 },
        light: { h: 270, s: 60, l: 96 },
        bg: { h: 270, s: 10, l: 98 },
        border: { h: 270, s: 15, l: 88 },
        hover: { h: 270, s: 15, l: 96 },
        icon: { h: 270, s: 60, l: 50 },
        text: { h: 270, s: 15, l: 25 },
      },
      dark: {
        primary: { h: 270, s: 60, l: 65 },
        light: { h: 270, s: 35, l: 25 },
        bg: { h: 270, s: 15, l: 8 },
        border: { h: 270, s: 20, l: 20 },
        hover: { h: 270, s: 20, l: 15 },
        icon: { h: 270, s: 60, l: 70 },
        text: { h: 270, s: 20, l: 80 },
      },
    },
  }

/**
 * Function color themes with HSL values
 */
export const FUNCTION_COLOR_THEMES: Record<FunctionType, ColorTheme> = {
  documents: {
    light: {
      primary: { h: 100, s: 42, l: 45 },
      light: { h: 100, s: 100, l: 97 },
      bg: { h: 100, s: 60, l: 98 },
      border: { h: 100, s: 45, l: 85 },
      hover: { h: 100, s: 45, l: 95 },
      icon: { h: 100, s: 42, l: 40 },
      text: { h: 100, s: 42, l: 30 },
      gradientFrom: { h: 100, s: 45, l: 90 },
      gradientTo: { h: 142, s: 45, l: 88 },
    },
    dark: {
      primary: { h: 100, s: 50, l: 55 },
      light: { h: 100, s: 30, l: 25 },
      bg: { h: 100, s: 25, l: 15 },
      border: { h: 100, s: 35, l: 30 },
      hover: { h: 100, s: 30, l: 20 },
      icon: { h: 100, s: 50, l: 60 },
      text: { h: 100, s: 50, l: 70 },
      gradientFrom: { h: 100, s: 30, l: 20 },
      gradientTo: { h: 142, s: 30, l: 18 },
    },
  },
  discussions: {
    light: {
      primary: { h: 280, s: 65, l: 50 },
      light: { h: 280, s: 100, l: 97 },
      bg: { h: 280, s: 70, l: 98 },
      border: { h: 280, s: 50, l: 85 },
      hover: { h: 280, s: 50, l: 95 },
      icon: { h: 280, s: 65, l: 45 },
      text: { h: 280, s: 65, l: 35 },
      gradientFrom: { h: 280, s: 50, l: 90 },
      gradientTo: { h: 300, s: 50, l: 88 },
    },
    dark: {
      primary: { h: 280, s: 70, l: 65 },
      light: { h: 280, s: 30, l: 25 },
      bg: { h: 280, s: 25, l: 15 },
      border: { h: 280, s: 35, l: 30 },
      hover: { h: 280, s: 30, l: 20 },
      icon: { h: 280, s: 70, l: 70 },
      text: { h: 280, s: 70, l: 80 },
      gradientFrom: { h: 280, s: 30, l: 20 },
      gradientTo: { h: 300, s: 30, l: 18 },
    },
  },
  activity: {
    light: {
      primary: { h: 220, s: 15, l: 45 },
      light: { h: 220, s: 20, l: 97 },
      bg: { h: 220, s: 20, l: 98 },
      border: { h: 220, s: 15, l: 85 },
      hover: { h: 220, s: 15, l: 95 },
      icon: { h: 220, s: 15, l: 40 },
      text: { h: 220, s: 15, l: 30 },
      gradientFrom: { h: 220, s: 15, l: 90 },
      gradientTo: { h: 215, s: 15, l: 88 },
    },
    dark: {
      primary: { h: 220, s: 15, l: 60 },
      light: { h: 220, s: 15, l: 25 },
      bg: { h: 220, s: 15, l: 15 },
      border: { h: 220, s: 15, l: 30 },
      hover: { h: 220, s: 15, l: 20 },
      icon: { h: 220, s: 15, l: 65 },
      text: { h: 220, s: 15, l: 75 },
      gradientFrom: { h: 220, s: 15, l: 20 },
      gradientTo: { h: 215, s: 15, l: 18 },
    },
  },
  settings: {
    light: {
      primary: { h: 215, s: 25, l: 45 },
      light: { h: 215, s: 30, l: 97 },
      bg: { h: 215, s: 30, l: 98 },
      border: { h: 215, s: 25, l: 85 },
      hover: { h: 215, s: 25, l: 95 },
      icon: { h: 215, s: 25, l: 40 },
      text: { h: 215, s: 25, l: 30 },
    },
    dark: {
      primary: { h: 215, s: 20, l: 60 },
      light: { h: 215, s: 15, l: 25 },
      bg: { h: 215, s: 15, l: 15 },
      border: { h: 215, s: 15, l: 30 },
      hover: { h: 215, s: 15, l: 20 },
      icon: { h: 215, s: 20, l: 65 },
      text: { h: 215, s: 20, l: 75 },
    },
  },
  finance: {
    light: {
      primary: { h: 36, s: 93, l: 53 },
      light: { h: 36, s: 100, l: 97 },
      bg: { h: 36, s: 100, l: 98 },
      border: { h: 36, s: 70, l: 85 },
      hover: { h: 36, s: 70, l: 95 },
      icon: { h: 36, s: 93, l: 45 },
      text: { h: 36, s: 93, l: 30 },
    },
    dark: {
      primary: { h: 36, s: 90, l: 65 },
      light: { h: 36, s: 30, l: 25 },
      bg: { h: 36, s: 25, l: 15 },
      border: { h: 36, s: 35, l: 30 },
      hover: { h: 36, s: 30, l: 20 },
      icon: { h: 36, s: 90, l: 70 },
      text: { h: 36, s: 90, l: 80 },
    },
  },
  team: {
    light: {
      primary: { h: 180, s: 55, l: 45 },
      light: { h: 180, s: 100, l: 97 },
      bg: { h: 180, s: 100, l: 98 },
      border: { h: 180, s: 55, l: 85 },
      hover: { h: 180, s: 55, l: 95 },
      icon: { h: 180, s: 55, l: 40 },
      text: { h: 180, s: 55, l: 30 },
    },
    dark: {
      primary: { h: 180, s: 60, l: 60 },
      light: { h: 180, s: 30, l: 25 },
      bg: { h: 180, s: 25, l: 15 },
      border: { h: 180, s: 35, l: 30 },
      hover: { h: 180, s: 30, l: 20 },
      icon: { h: 180, s: 60, l: 65 },
      text: { h: 180, s: 60, l: 75 },
    },
  },
}

/**
 * Legacy Tailwind color mappings for backward compatibility
 */
export const MODULE_COLORS: Partial<
  Record<ModuleTypeEnum, { bg: string; border: string; text: string }>
> = {
  [ModuleTypeEnum.PROJECT]: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  [ModuleTypeEnum.TASK]: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
  },
  [ModuleTypeEnum.TICKET]: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-700',
  },
  [ModuleTypeEnum.TASK_GROUP]: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-700',
  },
  [ModuleTypeEnum.ENTITY]: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  [ModuleTypeEnum.DEAL]: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
  },
  [ModuleTypeEnum.EVENT]: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
  },
  [ModuleTypeEnum.CONTRACT]: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  [ModuleTypeEnum.INVOICE]: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-700',
  },
  [ModuleTypeEnum.EXPENSE]: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
  },
  [ModuleTypeEnum.THREAD]: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  [ModuleTypeEnum.THREAD_COMMENT]: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  [ModuleTypeEnum.TIME_ENTRY]: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
  },
  [ModuleTypeEnum.TEAM]: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
  },
  [ModuleTypeEnum.INVENTORY]: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-700',
  },
  [ModuleTypeEnum.SUPPLIER]: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  [ModuleTypeEnum.MEMBER]: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-700',
  },
  [ModuleTypeEnum.DOCUMENT]: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
  },
  [ModuleTypeEnum.ORGANIZATION]: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-700',
  },
  [ModuleTypeEnum.TODAY]: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-700',
  },
  [ModuleTypeEnum.CUSTOM_FIELD]: {
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pink-700',
  },
  [ModuleTypeEnum.CUSTOM_COLLECTION]: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-700',
  },
  [ModuleTypeEnum.PERMISSION]: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
  },
  [ModuleTypeEnum.PERMISSION_SET]: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
  },
  [ModuleTypeEnum.FEATURE_FLAG]: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
  },
  [ModuleTypeEnum.INTEGRATION]: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  [ModuleTypeEnum.AI]: {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    text: 'text-violet-700',
  },
  [ModuleTypeEnum.NOTE]: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
  },
  [ModuleTypeEnum.CONTACT]: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-700',
  },
  [ModuleTypeEnum.PROFILE]: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  [ModuleTypeEnum.CALENDAR]: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-700',
  },
  [ModuleTypeEnum.CYCLE_INSTANCE]: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
  [ModuleTypeEnum.CYCLE_TEMPLATE]: {
    bg: 'bg-fuchsia-50',
    border: 'border-fuchsia-200',
    text: 'text-fuchsia-700',
  },
  [ModuleTypeEnum.FORM]: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  [ModuleTypeEnum.FORM_SUBMISSION]: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  [ModuleTypeEnum.WORKFLOW]: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  [ModuleTypeEnum.ANALYTICS]: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
  },
}

/**
 * Get the display name for a module
 */
export function getModuleDisplayName(
  moduleType: ModuleTypeEnum | string,
): string {
  if (typeof moduleType === 'string' && moduleType in ModuleTypeEnum) {
    return MODULE_DISPLAY_NAMES[moduleType as ModuleTypeEnum] || moduleType
  }
  return (
    MODULE_DISPLAY_NAMES[moduleType as ModuleTypeEnum] || String(moduleType)
  )
}

/**
 * Get the display name for a function
 */
export function getFunctionDisplayName(functionType: FunctionType): string {
  return FUNCTION_DISPLAY_NAMES[functionType] || functionType
}

/**
 * Helper to get user-friendly module name from technical permission name
 */
export function getFriendlyModuleNameFromPermission(
  permissionName: string,
): string {
  // Check for specific names like "Time - Approve"
  if (permissionName.startsWith('Time -')) {
    return 'Time'
  }

  if (permissionName.startsWith('Discussions -')) {
    return 'Discussions'
  }

  // Extract module name from patterns like "Project - Full Access"
  const parts = permissionName.split(' - ')
  if (parts.length > 1) {
    const modulePart = parts[0] || ''

    // Convert module name to corresponding enum if possible
    for (const [enumKey, displayName] of Object.entries(MODULE_DISPLAY_NAMES)) {
      if (
        modulePart.toLowerCase() === displayName.toLowerCase() ||
        modulePart.toLowerCase() === enumKey.toLowerCase()
      ) {
        return displayName
      }
    }

    return modulePart // Return original module part if no match
  }

  return permissionName // Return original if no pattern match
}

/**
 * Convert HSL object to HSL string
 */
export function hslToString(
  hsl: { h: number; s: number; l: number },
  alpha?: number,
): string {
  if (alpha !== undefined) {
    return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${alpha})`
  }
  return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`
}

/**
 * Convert HSL object to CSS variable format (space-separated)
 */
export function hslToCssVar(hsl: { h: number; s: number; l: number }): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`
}

/**
 * Get raw color values for a module (useful for React Native)
 */
export function getModuleColors(
  moduleType: ModuleTypeEnum,
  theme: 'light' | 'dark' = 'light',
): ColorScheme {
  return (
    MODULE_COLOR_THEMES[moduleType]?.[theme] ||
    MODULE_COLOR_THEMES[ModuleTypeEnum.PROJECT]![theme]
  )
}

/**
 * Get raw color values for a function (useful for React Native)
 */
export function getFunctionColors(
  functionType: FunctionType,
  theme: 'light' | 'dark' = 'light',
): ColorScheme {
  return (
    FUNCTION_COLOR_THEMES[functionType]?.[theme] ||
    FUNCTION_COLOR_THEMES['documents'][theme]
  )
}

/**
 * Get CSS variable-based class name for a module
 */
export function getModuleColorClass(moduleType: ModuleTypeEnum): string {
  const moduleMap: Partial<Record<ModuleTypeEnum, string>> = {
    [ModuleTypeEnum.PROJECT]: 'module-projects',
    [ModuleTypeEnum.ENTITY]: 'module-entities',
    [ModuleTypeEnum.TASK]: 'module-tasks',
    [ModuleTypeEnum.TICKET]: 'module-tickets',
    [ModuleTypeEnum.TASK_GROUP]: 'module-tasks',
    [ModuleTypeEnum.DEAL]: 'module-deals',
    [ModuleTypeEnum.EVENT]: 'module-events',
    [ModuleTypeEnum.CONTRACT]: 'module-contracts',
    [ModuleTypeEnum.INVOICE]: 'module-invoices',
    [ModuleTypeEnum.EXPENSE]: 'module-expenses',
    [ModuleTypeEnum.THREAD]: 'module-discussions',
    [ModuleTypeEnum.TIME_ENTRY]: 'module-time',
    [ModuleTypeEnum.TEAM]: 'module-team',
    [ModuleTypeEnum.INVENTORY]: 'module-inventory',
    [ModuleTypeEnum.MEMBER]: 'module-members',
    [ModuleTypeEnum.DOCUMENT]: 'module-documents',
    [ModuleTypeEnum.ORGANIZATION]: 'module-organization',
    [ModuleTypeEnum.TODAY]: 'module-today',
    [ModuleTypeEnum.INTEGRATION]: 'module-integrations',
    [ModuleTypeEnum.AI]: 'module-ai',
    [ModuleTypeEnum.NOTE]: 'module-notes',
    [ModuleTypeEnum.CONTACT]: 'module-contacts',
    [ModuleTypeEnum.WORKFLOW]: 'module-workflows',
    [ModuleTypeEnum.ANALYTICS]: 'module-analytics',
  }
  return moduleMap[moduleType] || 'module-projects'
}

/**
 * Get CSS variable-based class name for a function
 */
export function getFunctionColorClass(functionType: FunctionType): string {
  return `function-${functionType}`
}

/**
 * Get all CSS classes for a module
 */
export function getModuleClasses(moduleType: ModuleTypeEnum) {
  const baseClass = getModuleColorClass(moduleType)
  return {
    container: `${baseClass} module-card`,
    headerIcon: `${baseClass} module-header-icon`,
    text: `${baseClass} module-text-primary`,
    icon: `${baseClass} module-icon-primary`,
    button: `${baseClass} module-button`,
    hoverItem: `${baseClass} module-hover-item`,
  }
}

/**
 * Get all CSS classes for a function
 */
export function getFunctionClasses(functionType: FunctionType) {
  const baseClass = getFunctionColorClass(functionType)
  return {
    container: `${baseClass} function-card`,
    headerIcon: `${baseClass} function-header-icon`,
    text: `${baseClass} function-text-primary`,
    icon: `${baseClass} function-icon-primary`,
    button: `${baseClass} function-button`,
    hoverItem: `${baseClass} function-hover-item`,
  }
}

/**
 * Generate React Native style object for module colors
 */
export function getModuleStyleObject(
  moduleType: ModuleTypeEnum,
  theme: 'light' | 'dark' = 'light',
) {
  const colors = getModuleColors(moduleType, theme)
  return {
    container: {
      backgroundColor: hslToString(colors.bg, 0.4),
      borderColor: hslToString(colors.border, 0.6),
    },
    headerIcon: {
      backgroundColor: hslToString(colors.light),
      borderColor: hslToString(colors.border, 0.8),
      color: hslToString(colors.icon),
    },
    text: {
      color: hslToString(colors.text),
    },
    icon: {
      color: hslToString(colors.icon),
    },
    button: {
      backgroundColor: hslToString(colors.hover),
      color: hslToString(colors.primary),
    },
    hover: {
      backgroundColor: hslToString(colors.hover, 0.6),
      borderColor: hslToString(colors.border, 0.6),
    },
  }
}

/**
 * Generate React Native style object for function colors
 */
export function getFunctionStyleObject(
  functionType: FunctionType,
  theme: 'light' | 'dark' = 'light',
) {
  const colors = getFunctionColors(functionType, theme)
  const styles: any = {
    container: {
      backgroundColor: hslToString(colors.bg, 0.4),
      borderColor: hslToString(colors.border, 0.6),
    },
    headerIcon: {
      borderColor: hslToString(colors.border, 0.8),
      color: hslToString(colors.icon),
    },
    text: {
      color: hslToString(colors.text),
    },
    icon: {
      color: hslToString(colors.icon),
    },
    button: {
      backgroundColor: hslToString(colors.hover),
      color: hslToString(colors.primary),
    },
    hover: {
      backgroundColor: hslToString(colors.hover, 0.6),
      borderColor: hslToString(colors.border, 0.6),
    },
  }

  // Add gradient background for header icon if available
  if (colors.gradientFrom && colors.gradientTo) {
    styles.headerIcon.backgroundGradient = {
      colors: [
        hslToString(colors.gradientFrom),
        hslToString(colors.gradientTo),
      ],
      start: { x: 0, y: 0 },
      end: { x: 1, y: 1 },
    }
  } else {
    styles.headerIcon.backgroundColor = hslToString(colors.light)
  }

  return styles
}

/**
 * Generate CSS custom properties for all module and function colors
 * This can be used to inject color variables into a style tag
 */
export function generateColorCssVariables(): string {
  let css = ':root {\n'

  // Generate module colors
  Object.entries(MODULE_COLOR_THEMES).forEach(([moduleType, theme]) => {
    const moduleClass = getModuleColorClass(moduleType as ModuleTypeEnum)
    const varPrefix = moduleClass.replace('module-', '--module-')

    // Light mode
    Object.entries(theme.light).forEach(([colorKey, colorValue]) => {
      css += `  ${varPrefix}-${colorKey}: ${hslToCssVar(colorValue as any)};\n`
    })
  })

  // Generate function colors
  Object.entries(FUNCTION_COLOR_THEMES).forEach(([functionType, theme]) => {
    const varPrefix = `--function-${functionType}`

    // Light mode
    Object.entries(theme.light).forEach(([colorKey, colorValue]) => {
      css += `  ${varPrefix}-${colorKey}: ${hslToCssVar(colorValue as any)};\n`
    })
  })

  css += '}\n\n'

  // Dark mode
  css += '.dark {\n'

  // Generate dark mode module colors
  Object.entries(MODULE_COLOR_THEMES).forEach(([moduleType, theme]) => {
    const moduleClass = getModuleColorClass(moduleType as ModuleTypeEnum)
    const varPrefix = moduleClass.replace('module-', '--module-')

    Object.entries(theme.dark).forEach(([colorKey, colorValue]) => {
      css += `  ${varPrefix}-${colorKey}: ${hslToCssVar(colorValue as any)};\n`
    })
  })

  // Generate dark mode function colors
  Object.entries(FUNCTION_COLOR_THEMES).forEach(([functionType, theme]) => {
    const varPrefix = `--function-${functionType}`

    Object.entries(theme.dark).forEach(([colorKey, colorValue]) => {
      css += `  ${varPrefix}-${colorKey}: ${hslToCssVar(colorValue as any)};\n`
    })
  })

  css += '}'

  return css
}

/**
 * Generate CSS class definitions for module colors
 * @deprecated Use getModuleClasses or CSS variables instead
 */
export function generateModuleColorStyles(): string {
  let styles = ''

  Object.entries(MODULE_COLORS).forEach(([moduleType, colors]) => {
    styles += `
    .badge-${moduleType} {
      background-color: ${colors.bg};
      border-color: ${colors.border};
      color: ${colors.text};
    }
    `
  })

  return styles
}
